import type { IconName } from "../../components/Icon/Icon.tsx";
import type { ModelType } from "../types.ts";
import {
  ANIMATE_GENERATION_TYPE,
  BRIEF_TO_IMAGE_GENERATION_TYPE,
  COPY_AD_GENERATION_TYPE,
  EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE,
  ENHANCE_GENERATION_TYPE,
  EXPAND_GENERATION_TYPE,
  GENERATIVE_FILL_GENERATION_TYPE,
  IMAGE_TO_IMAGE_GENERATION_TYPE,
  STYLE_TRANSFER_GENERATION_TYPE,
  TEXT_TO_IMAGE_GENERATION_TYPE,
  UPSCALER_GENERATION_TYPE,
} from "./constants.ts";
import type {
  BackgroundReplacementMode,
  EditAssetPanelType,
  GenerationRatio,
  GenerationType,
  RatioDisplay,
} from "./types.ts";

// XXX: Those tool names are used for tracking purposes (mixpanel `generation_type` property),
// and match the ones defined in the backend.
export const TOOL_TRACKING_NAMES: Record<GenerationType, string> = {
  [TEXT_TO_IMAGE_GENERATION_TYPE]: "Create",
  [STYLE_TRANSFER_GENERATION_TYPE]: "StyleTransfer",
  [EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE]: "Edit",
  [IMAGE_TO_IMAGE_GENERATION_TYPE]: "Variation",
  [GENERATIVE_FILL_GENERATION_TYPE]: "Fill",
  [UPSCALER_GENERATION_TYPE]: "Upscale",
  [BRIEF_TO_IMAGE_GENERATION_TYPE]: "Brief",
  [EXPAND_GENERATION_TYPE]: "Expand",
  [ENHANCE_GENERATION_TYPE]: "Enhance",
  [ANIMATE_GENERATION_TYPE]: "Animate",
  [COPY_AD_GENERATION_TYPE]: "Copy ad",
} as const;

export const MODEL_TYPE_ICON_NAMES: Record<ModelType, IconName> = {
  STYLE: "Droplets",
  CHARACTER: "PersonStanding",
  OBJECT: "Shirt",
} as const;

export const MODEL_TYPE_LABELS: Record<ModelType, string> = {
  STYLE: "Style",
  CHARACTER: "Character",
  OBJECT: "Object",
} as const;

export const MODEL_TYPE_LABELS_WITH_ARTICLE: Record<ModelType, string> = {
  STYLE: "a style",
  CHARACTER: "a character",
  OBJECT: "an object",
} as const;

export const TOOL_DISPLAYABLE_NAMES: Record<GenerationType, string> = {
  [TEXT_TO_IMAGE_GENERATION_TYPE]: "Create",
  [STYLE_TRANSFER_GENERATION_TYPE]: "Style transfer",
  [EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE]: "Edit",
  [IMAGE_TO_IMAGE_GENERATION_TYPE]: "Variation",
  [GENERATIVE_FILL_GENERATION_TYPE]: "Fill",
  [UPSCALER_GENERATION_TYPE]: "Upscale",
  [BRIEF_TO_IMAGE_GENERATION_TYPE]: "Brief",
  [EXPAND_GENERATION_TYPE]: "Expand",
  [ENHANCE_GENERATION_TYPE]: "Enhance",
  [ANIMATE_GENERATION_TYPE]: "Animate",
  [COPY_AD_GENERATION_TYPE]: "Copy ad",
} as const;

export const GENERATION_RATIO_DISPLAY: Record<GenerationRatio, RatioDisplay> = {
  r_1_1: {
    label: "1:1",
    iconName: "Square",
    extraInfo: "Instagram post",
  },
  r_9_16: {
    label: "9:16",
    iconName: "RectangleVertical",
    extraInfo: "Instagram story",
  },
  r_4_5: {
    label: "4:5",
    iconName: "RectangleVertical",
    extraInfo: "Instagram post",
  },
  r_3_4: {
    label: "3:4",
    iconName: "RectangleVertical",
    extraInfo: "Portrait",
  },
  r_191_100: {
    label: "1.91:1",
    iconName: "RectangleHorizontal",
    extraInfo: "Instagram post",
  },
  r_16_9: {
    label: "16:9",
    iconName: "RectangleHorizontal",
    extraInfo: "Cover",
  },
  r_4_3: {
    label: "4:3",
    iconName: "RectangleHorizontal",
    extraInfo: "Landscape",
  },
} as const;

export const EDIT_ASSET_PANEL_TYPE_TO_LABEL: Record<
  EditAssetPanelType,
  string
> = {
  INFO: "Information",
  TAGS: "Tags",
  DEBUG: "Debug",
  HISTORY: "Edition history",
};

export const GENERATION_TYPE_TO_LABEL_AND_ICON: Record<
  GenerationType,
  { label: string; iconName: IconName }
> = {
  [TEXT_TO_IMAGE_GENERATION_TYPE]: { label: "Generate", iconName: "Sparkles" },
  [EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE]: {
    label: "Edit",
    iconName: "SquarePen",
  },
  [IMAGE_TO_IMAGE_GENERATION_TYPE]: {
    label: "Variation",
    iconName: "Layers",
  },
  [STYLE_TRANSFER_GENERATION_TYPE]: {
    label: "Style transfer",
    iconName: "ArrowLeftRight",
  },
  [UPSCALER_GENERATION_TYPE]: { label: "Upscale", iconName: "ImageUpscale" },
  [GENERATIVE_FILL_GENERATION_TYPE]: { label: "Fill", iconName: "LassoSelect" },
  [BRIEF_TO_IMAGE_GENERATION_TYPE]: { label: "Brief", iconName: "SquareStack" },
  [EXPAND_GENERATION_TYPE]: { label: "Expand", iconName: "Expand" },
  [ENHANCE_GENERATION_TYPE]: { label: "Enhance", iconName: "WandSparkles" },
  [ANIMATE_GENERATION_TYPE]: { label: "Animate", iconName: "Video" },
  [COPY_AD_GENERATION_TYPE]: { label: "Copy ad", iconName: "Images" },
};

export const BACKGROUND_REPLACEMENT_MODE_TO_LABEL: Record<
  BackgroundReplacementMode,
  string
> = {
  IDEOGRAM_REPLACE_BACKGROUND: "Ideogram",
  FLUX_PIPELINE: "Flux",
} as const;
